/**
 * Tools for picking a random MLS photo to use as a *blurred* placeholder
 * on property cards + the property-info gallery.
 *
 * Why random + blurred: pre-8727224 we showed the raw MLS first photo as
 * the property hero, which made unclaimed cards look like the seller's
 * own marketing. The branded `cardPlaceholderImg` replaced that with a
 * single logo, but every card / gallery then looked identical. The
 * blurred-random approach restores variety + a hint of the actual house
 * without pretending to be a curated listing photo.
 *
 * Stable random — same propertyId always picks the same MLS photo, so
 * the placeholder doesn't flicker between renders / reloads / route
 * transitions. The seed defaults to "" which deterministically picks
 * the first photo; callers should pass the property id.
 */

export interface MlsPhotoLike {
  url?: string;
}

export interface MlsPhotosContainer {
  mlsHasPhotoLists?: MlsPhotoLike[];
}

/**
 * Static pool of stock house photos used as a guaranteed-variety
 * fallback when the backend's `mlsHasPhotoLists` is empty (which is
 * currently the default state for freshly-fetched REAPI properties —
 * see audit-findings-backlog #89).
 *
 * Source: Unsplash royalty-free, hot-linkable. Working immediately
 * with zero infrastructure. To swap to self-hosted images:
 *   1. Upload 8-12 house images to s3://simplerhome-public/placeholders/
 *   2. Replace each URL below with the corresponding bucket URL
 *      (e.g. https://simplerhome-public.s3.us-east-2.amazonaws.com/placeholders/house-1.webp)
 * No other code changes needed — pickRandomMlsPhoto draws from this
 * array, hashed stably by propertyId so each card lands on the same
 * photo every reload.
 */
const buildUnsplashUrl = (photoId: string): string =>
  `https://images.unsplash.com/photo-${photoId}?w=900&q=70`;

// Unsplash photo IDs — royalty-free, hot-link-safe, all verified
// 2026-06-15 to (a) return HTTP 200 and (b) actually depict a house
// or home interior. Mix of single-family exteriors, apartment buildings,
// living rooms, dining rooms, kitchens — gives natural visual variety
// across cards.
//
// Verification process: curl each URL, drop 12 that returned 404, then
// visually inspect the remaining 38 and drop 3 non-house outliers
// (abstract art, blueprint sketch, person in field). Audit notes live in
// the project's PRs / runbook — re-run the check yearly in case any
// Unsplash photo is later taken down by its author.
const UNSPLASH_PHOTO_IDS: ReadonlyArray<string> = [
  "1568605114967-8130f3a36994", // night-lit suburban two-story
  "1570129477492-45c003edd2be", // grey clapboard with wrap-around porch
  "1583608205776-bfd35f0d9f83", // craftsman cottage with palms
  "1572120360610-d971b9d7767c", // autumn porch with pumpkins
  "1564013799919-ab600027ffc6", // white pool villa
  "1605276374104-dee2a0ed3cd6", // brick two-story with garage
  "1600596542815-ffad4c1539a9", // modern villa with infinity pool
  "1600585154340-be6161a56a0c", // dark timber modern house
  "1600607687939-ce8a6c25118c", // open-plan living room
  "1600566753190-17f0baa2a6c3", // angular modern with garden
  "1512917774080-9991f1c4c750", // poolside modern house
  "1519643381401-22c77e60520e", // dining room with hanging lights
  "1523217582562-09d0def993a6", // white modernist exterior
  "1544984243-ec57ea16fe25", // sunset desert pool house
  "1545324418-cc1a3fa10c00", // dark apartment block
  "1558036117-15d82a90b9b1", // farmhouse at golden hour
  "1560448204-e02f11c3d0e2", // beige open living room
  "1560185007-cde436f6a4d0", // dining room with wood floors
  "1560185009-5bf9f2849488", // living room with wood floors
  "1560185127-6ed189bf02f4", // beige living room with sofa
  "1560448075-bb485b067938", // classic white bathroom
  "1560448075-cbc16bb4af8e", // kitchen with breakfast bar
  "1571055107559-3e67626fa8be", // log cabin in autumn
  "1571939228382-b2f2b585ce15", // white estate with garden
  "1575517111478-7f6afd0973db", // villa with wooden deck
  "1592595896616-c37162298647", // small white cottage
  "1593696140826-c58b021acf8b", // grey kitchen-living space
  "1597047084897-51e81819a499", // white apartment block
  "1598228723793-52759bba239c", // brick suburban home
  "1600210491892-03d54c0aaf87", // farmhouse living room
  "1600210492486-724fe5c67fb0", // living room with photo gallery
  "1600210492493-0946911123ea", // open-plan modern living
  "1600573472556-e636c2acda88", // dotted-screen pool house at night
  "1600585152915-d208bec867a1", // modern living room with pool view
  "1600585154526-990dced4db0d", // dark modern house exterior at dusk
];

/**
 * 50 stock house photos via Unsplash CDN. Stable per-propertyId pick
 * means every card on a listing page lands on a different image without
 * flickering across navigations.
 */
export const PLACEHOLDER_POOL: ReadonlyArray<string> =
  UNSPLASH_PHOTO_IDS.map(buildUnsplashUrl);

/**
 * djb2-ish string-to-int hash. Cheap, no dependencies, good enough for
 * spreading propertyIds across N photos. Always returns a non-negative
 * integer.
 */
const stableHash = (seed: string): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0; // force int32
  }
  return Math.abs(hash);
};

/**
 * Pick a deterministic-random photo URL for the supplied property,
 * preferring real MLS data and falling back to the static pool.
 *
 * Source priority:
 *   1. `mls.mlsHasPhotoLists[*]` — real per-property photo when present
 *   2. `PLACEHOLDER_POOL` — guaranteed-variety stock house image
 *
 * Returns undefined only when both sources are empty (i.e., the pool is
 * misconfigured to an empty array AND the property has no MLS).
 */
export const pickRandomMlsPhoto = (
  mls: MlsPhotosContainer | undefined | null,
  seed: string | number = ""
): string | undefined => {
  const seedStr = String(seed);
  const seedHash = stableHash(seedStr);

  const photos = mls?.mlsHasPhotoLists;
  if (Array.isArray(photos) && photos.length > 0) {
    const idx = seedHash % photos.length;
    const url = photos[idx]?.url;
    if (url) return url;
  }

  if (PLACEHOLDER_POOL.length === 0) return undefined;
  return PLACEHOLDER_POOL[seedHash % PLACEHOLDER_POOL.length];
};
